import React, { useEffect, useState } from 'react';
import { AlertCircle, Download, Film, Loader2, RefreshCw } from 'lucide-react';
import MobileCard from '../../camera/components/mobileCard/MobileCard';
import { getEditorVideoDownloadUrl } from '../api/videoEditor.api';
import { STATUS_STYLES, formatDateTime } from '../utils/editorVideoFormat';

const SOURCE_DETAIL_LABELS = {
  period: 'Período',
  orientation: 'Orientación',
  quality: 'Calidad',
  durationSeconds: 'Duración',
  intervalMinutes: 'Intervalo',
};

const formatSourceDetailValue = (key, value) => {
  if (key === 'durationSeconds') return `${value}s`;
  return String(value);
};

const SourceDetails = ({ sourceDetails }) => {
  const entries = Object.entries(sourceDetails || {});
  if (entries.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {entries.map(([key, value]) => (
        <span
          key={key}
          className="text-[11px] font-medium text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full"
        >
          {SOURCE_DETAIL_LABELS[key] || key}: {formatSourceDetailValue(key, value)}
        </span>
      ))}
    </div>
  );
};

// The list is owned by the parent (shared with the thumbnails carousel); this tab
// only asks for a fresh copy each time it is opened.
const CreatedVideosTab = ({ ftpUser, jobs, loading, error, onRefresh }) => {
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadErrors, setDownloadErrors] = useState({});

  useEffect(() => {
    onRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownload = async (jobId) => {
    setDownloadingId(jobId);
    setDownloadErrors((prev) => ({ ...prev, [jobId]: null }));
    try {
      const url = await getEditorVideoDownloadUrl(jobId, ftpUser);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Error downloading editor video:', err);
      setDownloadErrors((prev) => ({ ...prev, [jobId]: 'No se pudo generar el enlace de descarga.' }));
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="space-y-4 animate-fadeIn">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">Videos generados en los últimos 30 días.</p>
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-gray-800 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      {loading && (
        <MobileCard padding="p-6">
          <div className="flex flex-col items-center gap-2 text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin" />
            <p className="text-sm">Cargando videos...</p>
          </div>
        </MobileCard>
      )}

      {!loading && error && (
        <MobileCard className="bg-red-50 border-red-200" padding="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </MobileCard>
      )}

      {!loading && !error && jobs.length === 0 && (
        <MobileCard padding="p-6">
          <div className="flex flex-col items-center gap-2 text-gray-400">
            <Film className="w-6 h-6" />
            <p className="text-sm">No hay videos creados en los últimos 30 días.</p>
          </div>
        </MobileCard>
      )}

      {!loading && !error && jobs.length > 0 && (
        <div className="space-y-3">
          {jobs.map((job) => (
            <MobileCard key={job.id} padding="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs text-gray-500">{formatDateTime(job.createdAt)}</p>
                  <SourceDetails sourceDetails={job.sourceDetails} />
                </div>
                <span className={`flex-shrink-0 text-xs font-bold px-3 py-1 rounded-full ${STATUS_STYLES[job.status] || 'bg-slate-100 text-slate-600'}`}>
                  {job.status}
                </span>
              </div>

              {job.status === 'COMPLETED' && (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => handleDownload(job.id)}
                    disabled={downloadingId === job.id}
                    className="flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors"
                  >
                    {downloadingId === job.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    Descargar
                  </button>
                  {downloadErrors[job.id] && (
                    <p className="mt-1.5 text-xs text-red-600">{downloadErrors[job.id]}</p>
                  )}
                </div>
              )}
            </MobileCard>
          ))}
        </div>
      )}
    </div>
  );
};

export default CreatedVideosTab;
